import { useEffect, useRef } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';

export const useDateRangeLogic = (
  startDateFieldName: string = 'startDate',
  endDateFieldName: string = 'endDate',
  methods?: UseFormReturn<any>,
) => {
  const formContext = useFormContext();
  const formMethods = methods || formContext;
  
  if (!formMethods) {
    throw new Error('useDateRangeLogic must be used within a FormProvider or receive methods as parameter');
  }
  
  const { watch, trigger, clearErrors } = formMethods;
  const { errors } = formMethods.formState;
  
  const startDate = watch(startDateFieldName as any) as string | null;
  const endDate = watch(endDateFieldName as any) as string | null;
  
  const prevStartDateRef = useRef<string | null>(startDate);
  const prevEndDateRef = useRef<string | null>(endDate);
  const lastChangedFieldRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (startDate !== prevStartDateRef.current) {
      prevStartDateRef.current = startDate;
      lastChangedFieldRef.current = startDateFieldName;
      trigger(startDateFieldName);
      trigger(endDateFieldName);
      setTimeout(() => {
        const endDateError = formMethods.formState.errors[endDateFieldName];
        if (endDateError?.type !== 'required') {
          clearErrors(endDateFieldName);
        }
      }, 0);
    }
  }, [startDate, trigger, startDateFieldName, endDateFieldName, formMethods, endDateFieldName, clearErrors]);
  
  useEffect(() => {
    if (endDate !== prevEndDateRef.current) {
      prevEndDateRef.current = endDate;
      lastChangedFieldRef.current = endDateFieldName;
      trigger(endDateFieldName);
      trigger(startDateFieldName);
      setTimeout(() => {
        const startDateError = formMethods.formState.errors[startDateFieldName];
        if (startDateError?.type !== 'required') {
          clearErrors(startDateFieldName);
        }
      }, 0);
    }
  }, [endDate, trigger, startDateFieldName, endDateFieldName, formMethods, startDateFieldName, clearErrors]);
  
  useEffect(() => {
    const lastChanged = lastChangedFieldRef.current;
    const endDateError = errors[endDateFieldName];
    const startDateError = errors[startDateFieldName];

    if (lastChanged === startDateFieldName && endDateError?.type !== 'required') {
      clearErrors(endDateFieldName);
    }
    if (lastChanged === endDateFieldName && startDateError?.type !== 'required') {
      clearErrors(startDateFieldName);
    }
  }, [errors, startDateFieldName, endDateFieldName, clearErrors]);
};
